import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useCollectionBinding,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, stagger } from '../utils/quinn';
import { byDist, directions, poiDesc, travel, website, type Poi } from '../utils/places';

/** Display order: the K–12 decision drivers first, then child care, higher education, libraries. */
const LEVELS: Array<[string, string]> = [
  ['elementary', 'Elementary'],
  ['middle', 'Middle school'],
  ['high', 'High school'],
  ['k12', 'K–12 & private'],
  ['preschool', 'Preschool & child care'],
  ['college', 'College & university'],
  ['library', 'Library'],
  ['school', 'School'],
];
const LEVEL_LABEL = Object.fromEntries(LEVELS);
const LEVEL_ORDER = Object.fromEntries(LEVELS.map(([k], i) => [k, i]));

/** Level from the name, then the description, then Google's place type. */
function levelOf(p: Poi): string {
  const n = p.poi_name || '';
  const d = poiDesc(p);
  const t = clean(p.poi_place_primary_type) || '';
  if (/library/i.test(n)) return 'library';
  if (/university|college|technical|institute/i.test(n)) return 'college';
  if (/middle school|junior high|intermediate/i.test(n)) return 'middle';
  if (/high school/i.test(n)) return 'high';
  if (/elementary|primary school/i.test(n)) return 'elementary';
  if (/pre-?school|pre-?k\b|child ?care|day ?care|early learning/i.test(n)) return 'preschool';
  if (/\bp?k-?12\b|through-?12th|12th[- ]grade|private school|catholic school|christian school|preparatory|independent school/i.test(d)) return 'k12';
  if (t === 'library') return 'library';
  if (t === 'university' || t === 'educational_institution' || t === 'college') return 'college';
  if (t === 'primary_school') return 'elementary';
  if (t === 'secondary_school') return 'high';
  if (t === 'preschool' || t === 'child_care_agency') return 'preschool';
  if (/middle school/i.test(d)) return 'middle';
  if (/high school/i.test(d)) return 'high';
  if (/elementary/i.test(d)) return 'elementary';
  if (/pre-?school|child-? ?care/i.test(d)) return 'preschool';
  if (/college|university/i.test(d)) return 'college';
  if (/library/i.test(d)) return 'library';
  return t === 'school' ? 'k12' : 'school';
}

export function PlaceActions({ p, row }: { p: Poi; row?: boolean }) {
  const d = directions(p);
  const w = website(p);
  if (!d && !w) return null;
  return (
    <div
      className={`flex flex-wrap gap-x-5 gap-y-1 [&_.textlink]:inline-flex [&_.textlink]:min-h-6 [&_.textlink]:items-center max-[767px]:gap-x-4 max-[767px]:gap-y-0 max-[767px]:[&_.textlink]:min-h-11 ${
        row ? 'justify-end pt-1.5 max-[767px]:ml-auto max-[767px]:pt-0' : 'mt-auto pt-2.5 max-[767px]:pt-0.5'
      }`}
    >
      {d && <a className="textlink" href={d} target="_blank" rel="noopener">Directions</a>}
      {w && <a className="textlink" href={w} target="_blank" rel="noopener">Website</a>}
    </div>
  );
}

export function SchoolsNearby() {
  const pois = useCollectionBinding<Poi>('schoolsnearby.schools', { collection: 'neighborhood_pois', filter: { poi_type: 'Education' } });

  const schools = pois
    .map((p) => ({ p, lv: levelOf(p) }))
    .sort((a, b) => LEVEL_ORDER[a.lv] - LEVEL_ORDER[b.lv] || byDist(a.p, b.p));
  if (!schools.length) return null;

  return (
    <section className="section section--surface schools" id="schools">
      <div className="section__head" data-reveal>
        <div className="section__lead-block">
          <RichTextContent path="schoolsnearby.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="schoolsnearby.title" as="h2" className="h2" />
        </div>
        <RichTextContent path="schoolsnearby.lead" as="div" className="section__lead" />
      </div>
      <EditableCollectionBinding bindingPath="schoolsnearby.schools" label="Schools" className="flex flex-col border-t border-t-[--q-secondary]">
        {schools.map(({ p, lv }, k) => (
          <div
            className="grid grid-cols-[96px_minmax(0,1fr)_200px] items-center gap-6 border-b border-b-[--q-border] py-[18px] [&_.chip]:border [&_.chip]:border-[--q-border] [&_.chip]:bg-[--q-paper] max-[1199px]:grid-cols-[96px_minmax(0,1fr)_176px] max-[767px]:grid-cols-[72px_minmax(0,1fr)] max-[767px]:items-start max-[767px]:gap-x-4 max-[767px]:gap-y-3 max-[767px]:py-4"
            key={`${p.poi_name}-${k}`}
            data-reveal
            style={stagger(k)}
          >
            <div className="relative h-[72px] w-24 overflow-hidden rounded-md border border-[--q-border] bg-[--q-paper] [&_img]:absolute [&_img]:inset-0 [&_img]:h-full [&_img]:w-full [&_img]:object-cover max-[767px]:h-[54px] max-[767px]:w-[72px]">
              {clean(p.poi_featured_image) ? (
                <VibesitesImage url={p.poi_featured_image!} alt={p.poi_name || ''} sizes="96px" />
              ) : (
                <div
                  className="absolute inset-0 [background:repeating-linear-gradient(135deg,transparent_0_6px,color-mix(in_srgb,var(--q-border)_60%,transparent)_6px_7px)]"
                  aria-hidden="true"
                />
              )}
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <div><span className="chip">{LEVEL_LABEL[lv]}</span></div>
              <h3 className="m-0 font-heading text-[22px] font-normal leading-7 max-[767px]:text-[19px] max-[767px]:leading-6">{p.poi_name}</h3>
              {poiDesc(p) && <p className="m-0 text-sm leading-5 text-[--q-muted] max-[767px]:text-[13px] max-[767px]:leading-[18px]">{poiDesc(p)}</p>}
            </div>
            <div className="flex flex-col items-end gap-0.5 text-right max-[767px]:col-[1/-1] max-[767px]:flex-row max-[767px]:flex-wrap max-[767px]:items-baseline max-[767px]:justify-start max-[767px]:gap-x-3 max-[767px]:gap-y-1 max-[767px]:text-left">
              <div className="font-heading text-[22px] leading-7 max-[767px]:text-[19px] max-[767px]:leading-6">{p.poi_miles_from_property || ''}</div>
              <div className="text-[13px] leading-[18px] text-[--q-muted]">{travel(p)}</div>
              <PlaceActions p={p} row />
            </div>
          </div>
        ))}
      </EditableCollectionBinding>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SchoolsNearby',
  displayName: 'Schools Nearby',
  tokens: {
    'schoolsnearby.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Schools in the area</p>' },
    'schoolsnearby.title': { label: 'Title', type: 'richtext', defaultValue: '<p>From preschool to college, a short drive from the driveway.</p>' },
    'schoolsnearby.lead': {
      label: 'Lead',
      type: 'richtext',
      defaultValue:
        '<p>The schools closest to <span data-type="field-token" data-field="property_name"></span>, elementary through high school first, nearest first within each level. Distances and drive times are from the community; attendance zones vary by address, so confirm with the district before you lease.</p>',
    },
  },
  collectionBindings: {
    'schoolsnearby.schools': {
      label: 'Schools',
      collection: 'neighborhood_pois',
      filter: { poi_type: 'Education' },
    },
  },
};

(SchoolsNearby as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default SchoolsNearby;
